// Миникарта зала: read-only SVG-план столов (вид сверху).
// Единственная копия на репозиторий: пакет @billiard/ui, потребители —
// backoffice / booking / tma-booking (раньше файл дублировался в каждом).
// Стили (.fm-*, .floor-map) остаются в globals.css приложений: тема у них
// намеренно разная, общая — только разметка и геометрия глифов.

/** Стол на плане: живые данные + геометрия (центр глифа, абстрактные единицы). */
export interface FloorMapTable {
  id: string
  name: string
  /** код типа игры (game_types.code); null = универсальный */
  kind: string | null
  x: number
  y: number
  /** 0 | 90 | 180 | 270 */
  rot: number
  /** индивидуальный масштаб стола, 0.5–2 (1 = базовый размер) */
  scale: number
}

export interface FloorMapData {
  canvas: { w: number; h: number }
  tables: FloorMapTable[]
}

/**
 * free — кликабелен; busy — занят (затемнён, бейдж); game — «в игре» (бэкофис);
 * inactive — контекст (другой тип игры и т.п.), не кликабелен.
 */
export type FloorTableStatus = 'free' | 'busy' | 'game' | 'inactive'

/** Канонический вид глифа для отрисовки. */
export type FloorGlyphKind = 'russian' | 'pool' | 'playstation' | 'generic'

/**
 * Определяет вид глифа по коду типа игры И по имени из справочника: коды
 * game_types генерируются из названий («Плейстейшен» → game-xxxx), поэтому
 * точного кода playstation/russian/pool у клуба может не быть — добираем
 * эвристикой по имени типа. Неопознанное — generic-прямоугольник цветом типа.
 */
export function resolveGlyphKind(
  kind: string | null,
  gameTypeName?: string | null
): FloorGlyphKind {
  if (kind === 'russian') return 'russian'
  if (kind === 'pool') return 'pool'
  if (kind === 'playstation') return 'playstation'
  const name = (gameTypeName ?? '').toLowerCase()
  if (/playstation|плейстейш|пристав|консол|xbox/.test(name)) return 'playstation'
  if (/русск|пирамид|russian/.test(name)) return 'russian'
  if (/pool|пул|америк/.test(name)) return 'pool'
  return 'generic'
}

/** Базовый масштаб глифов относительно базовых координат (столы крупнее — читаемее). */
const GLYPH_SCALE = 2

/** Габариты глифа (единицы канвы, с учётом GLYPH_SCALE и per-table scale; до поворота). */
export function tableFootprint(glyph: FloorGlyphKind, scale = 1): { w: number; h: number } {
  const base = (() => {
    switch (glyph) {
      case 'russian':
        return { w: 130, h: 65 }
      case 'pool':
        return { w: 100, h: 50 }
      case 'playstation':
        return { w: 110, h: 80 }
      default:
        return { w: 100, h: 55 }
    }
  })()
  const k = GLYPH_SCALE * scale
  return { w: base.w * k, h: base.h * k }
}

/** Русский бильярд: широкие «деревянные» борта, тёмное сукно, 6 луз. */
function RussianGlyph(): React.JSX.Element {
  const pockets: Array<[number, number]> = [
    [-54, -21.5],
    [54, -21.5],
    [-54, 21.5],
    [54, 21.5],
    [0, -23.5],
    [0, 23.5]
  ]
  return (
    <>
      <rect x={-65} y={-32.5} width={130} height={65} rx={7} className="fm-wood" />
      <rect x={-56} y={-23.5} width={112} height={47} rx={3} className="fm-felt-ru" />
      {pockets.map(([px, py], i) => (
        <circle key={i} cx={px} cy={py} r={4.5} className="fm-pocket" />
      ))}
    </>
  )
}

/** Американский пул: узкие борта, сукно посветлее, 6 луз, «даймонды» на бортах. */
function PoolGlyph(): React.JSX.Element {
  const pockets: Array<[number, number]> = [
    [-41, -16],
    [41, -16],
    [-41, 16],
    [41, 16],
    [0, -18],
    [0, 18]
  ]
  const diamonds: Array<[number, number]> = [
    [-23.5, -21.5],
    [23.5, -21.5],
    [-23.5, 21.5],
    [23.5, 21.5],
    [-46.5, 0],
    [46.5, 0]
  ]
  return (
    <>
      <rect x={-50} y={-25} width={100} height={50} rx={6} className="fm-wood fm-wood-pool" />
      <rect x={-43} y={-18} width={86} height={36} rx={3} className="fm-felt-pool" />
      {pockets.map(([px, py], i) => (
        <circle key={i} cx={px} cy={py} r={3.5} className="fm-pocket" />
      ))}
      {diamonds.map(([px, py], i) => (
        <path
          key={i}
          d={`M ${px} ${py - 2} L ${px + 2} ${py} L ${px} ${py + 2} L ${px - 2} ${py} Z`}
          className="fm-diamond"
        />
      ))}
    </>
  )
}

/**
 * PlayStation-зона: «ковёр», ТВ с символами △○✕□ на экране, диван и
 * DualSense-геймпад (белый корпус, тёмный тачпад и стики).
 */
function PlaystationGlyph(): React.JSX.Element {
  return (
    <>
      <rect x={-55} y={-40} width={110} height={80} rx={12} className="fm-ps-zone" />
      <rect x={-32} y={-37} width={64} height={9} rx={2} className="fm-ps-tv" />
      <g className="fm-ps-symbols" transform="translate(0 -32.5)">
        <path d="M -13.6 1.8 L -11.2 -2.1 L -8.8 1.8 Z" className="fm-ps-sym fm-ps-tri" />
        <circle cx={-3.7} cy={0} r={2} className="fm-ps-sym fm-ps-circ" />
        <g className="fm-ps-sym fm-ps-cross">
          <line x1={1.9} y1={-1.8} x2={5.5} y2={1.8} />
          <line x1={1.9} y1={1.8} x2={5.5} y2={-1.8} />
        </g>
        <rect x={9.2} y={-1.9} width={3.8} height={3.8} className="fm-ps-sym fm-ps-sq" />
      </g>
      <g transform="translate(0 -14)">
        <rect x={-14} y={-5.5} width={28} height={11} rx={5.5} className="fm-ps5-body" />
        <circle cx={-11} cy={2.5} r={4.5} className="fm-ps5-body" />
        <circle cx={11} cy={2.5} r={4.5} className="fm-ps5-body" />
        <rect x={-5} y={-5.5} width={10} height={5} rx={2} className="fm-ps5-pad" />
        <circle cx={-4.5} cy={2} r={2} className="fm-ps5-stick" />
        <circle cx={4.5} cy={2} r={2} className="fm-ps5-stick" />
      </g>
      <rect x={-30} y={10} width={60} height={16} rx={7} className="fm-ps-sofa" />
      <rect x={-40} y={6} width={9} height={24} rx={4} className="fm-ps-sofa" />
      <rect x={31} y={6} width={9} height={24} rx={4} className="fm-ps-sofa" />
      <rect x={-40} y={26} width={80} height={8} rx={4} className="fm-ps-sofa" />
    </>
  )
}

/** Прочие типы / универсальный стол: скруглённый прямоугольник цветом типа игры. */
function GenericGlyph({ color }: { color: string | null }): React.JSX.Element {
  return (
    <rect
      x={-50}
      y={-27.5}
      width={100}
      height={55}
      rx={10}
      className="fm-generic"
      style={color ? { fill: color, fillOpacity: 0.28, stroke: color } : undefined}
    />
  )
}

function glyphFor(glyph: FloorGlyphKind, color: string | null): React.JSX.Element {
  switch (glyph) {
    case 'russian':
      return <RussianGlyph />
    case 'pool':
      return <PoolGlyph />
    case 'playstation':
      return <PlaystationGlyph />
    default:
      return <GenericGlyph color={color} />
  }
}

const HIT_PAD = 10 // расширение тап-зоны вокруг глифа (телефон)

interface TableGlyphProps {
  table: FloorMapTable
  glyph: FloorGlyphKind
  color: string | null
  status: FloorTableStatus
  selected: boolean
  onSelect?: (id: string) => void
  onPointerDown?: (id: string, e: React.PointerEvent<SVGGElement>) => void
}

function TableGlyph({
  table,
  glyph,
  color,
  status,
  selected,
  onSelect,
  onPointerDown
}: TableGlyphProps): React.JSX.Element {
  const { w, h } = tableFootprint(glyph, table.scale)
  // Габариты с учётом поворота — для бейджа занятости.
  const effW = table.rot % 180 === 0 ? w : h
  const effH = table.rot % 180 === 0 ? h : w
  const clickable = status === 'free' && !!onSelect && !onPointerDown
  // Подпись обрезается по ширине глифа (~8 ед. канвы на символ при кегле 13).
  const labelMax = Math.max(10, Math.floor(effW / 8))
  const label = table.name.length > labelMax ? `${table.name.slice(0, labelMax - 1)}…` : table.name

  const select = (): void => {
    if (status === 'free') onSelect?.(table.id)
  }

  return (
    <g
      transform={`translate(${table.x} ${table.y})`}
      className={`fm-table is-${status}${selected ? ' is-selected' : ''}${clickable ? ' is-clickable' : ''}`}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={table.name}
      onClick={onPointerDown ? undefined : select}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                select()
              }
            }
          : undefined
      }
      onPointerDown={onPointerDown ? (e) => onPointerDown(table.id, e) : undefined}
    >
      <g transform={`rotate(${table.rot}) scale(${GLYPH_SCALE * table.scale})`}>
        {glyphFor(glyph, color)}
      </g>
      {/* Подпись не вращается — читается при любом повороте стола. */}
      <text className="fm-label" y={4}>
        {label}
      </text>
      {status === 'busy' && (
        <g className="fm-badge" transform={`translate(${effW / 2 - 6} ${-effH / 2 + 6})`}>
          <circle r={8} />
          <line x1={-3.5} y1={-3.5} x2={3.5} y2={3.5} />
          <line x1={-3.5} y1={3.5} x2={3.5} y2={-3.5} />
        </g>
      )}
      {/* Невидимая тап-зона; в состоянии selected её обводка — золотая рамка. */}
      <rect
        className="fm-hit"
        transform={`rotate(${table.rot})`}
        x={-w / 2 - HIT_PAD}
        y={-h / 2 - HIT_PAD}
        width={w + HIT_PAD * 2}
        height={h + HIT_PAD * 2}
        rx={10}
      />
    </g>
  )
}

export interface FloorMapProps {
  layout: FloorMapData
  /** Справочник типов игр клуба — цвет для generic-глифов. */
  gameTypes: ReadonlyArray<{ code: string; name: string; color: string | null }>
  /** Статус по id стола; не указан → 'free'. */
  statuses?: Record<string, FloorTableStatus>
  selectedId?: string | null
  /** Вызывается только для столов со статусом 'free'. */
  onSelect?: (id: string) => void
  /** Хук редактора: если задан, глифы получают pointerdown и не вызывают onSelect. */
  onTablePointerDown?: (id: string, e: React.PointerEvent<SVGGElement>) => void
  /**
   * Обрезать пустые поля канвы: viewBox строится по габаритам расставленных
   * столов. Клиентская карта подстраивается под экран, сохраняя раскладку
   * админа 1:1; редактор этот флаг не передаёт (работает с полной канвой).
   */
  fitToContent?: boolean
  className?: string
  /** Подложка (сетка редактора и т.п.) — рендерится под столами. */
  children?: React.ReactNode
}

export function FloorMap({
  layout,
  gameTypes,
  statuses,
  selectedId,
  onSelect,
  onTablePointerDown,
  fitToContent,
  className,
  children
}: FloorMapProps): React.JSX.Element {
  const typeByCode = new Map(gameTypes.map((g) => [g.code, g]))

  // viewBox: вся канва (редактор) либо bbox расставленных столов + отступ (клиенты).
  let viewBox = `0 0 ${layout.canvas.w} ${layout.canvas.h}`
  if (fitToContent && layout.tables.length > 0) {
    const PAD = 24
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const t of layout.tables) {
      const gt = t.kind ? typeByCode.get(t.kind) : undefined
      const fp = tableFootprint(resolveGlyphKind(t.kind, gt?.name), t.scale)
      const effW = t.rot % 180 === 0 ? fp.w : fp.h
      const effH = t.rot % 180 === 0 ? fp.h : fp.w
      minX = Math.min(minX, t.x - effW / 2)
      minY = Math.min(minY, t.y - effH / 2)
      maxX = Math.max(maxX, t.x + effW / 2)
      maxY = Math.max(maxY, t.y + effH / 2)
    }
    const x0 = Math.max(0, minX - PAD)
    const y0 = Math.max(0, minY - PAD)
    const x1 = Math.min(layout.canvas.w, maxX + PAD)
    const y1 = Math.min(layout.canvas.h, maxY + PAD)
    if (x1 > x0 && y1 > y0) viewBox = `${x0} ${y0} ${x1 - x0} ${y1 - y0}`
  }

  return (
    <svg
      className={`floor-map${className ? ` ${className}` : ''}`}
      viewBox={viewBox}
      role="img"
      aria-label="План зала"
      style={{ width: '100%', height: 'auto', display: 'block' }}
    >
      {children}
      {layout.tables.map((t) => {
        const gt = t.kind ? typeByCode.get(t.kind) : undefined
        return (
          <TableGlyph
            key={t.id}
            table={t}
            glyph={resolveGlyphKind(t.kind, gt?.name)}
            color={gt?.color ?? null}
            status={statuses?.[t.id] ?? 'free'}
            selected={selectedId === t.id}
            onSelect={onSelect}
            onPointerDown={onTablePointerDown}
          />
        )
      })}
    </svg>
  )
}
